import { CustomComment } from 'interfaces/talkplus.interface'
import { createContext, useContext, useRef, useState, useCallback } from 'react'
import { CommentStatus, CommentType } from 'interfaces/talkplus.interface'
import { Message } from 'talkplus-sdk'
import { useQuery } from 'react-query'
import { useChannel } from './ChannelProvider'
import { useTalkPlusClient } from 'contexts/TalkPlusClientContext'
import { userState } from 'store/user'
import { useRecoilValue } from 'recoil'

interface MessagesContextType {
    comments: CustomComment[]
    messagesContainerRef: React.RefObject<HTMLDivElement>
    errorCommentDeleteHandler: (deleteId: string) => void
    nextPageHandler: () => Promise<void>
    messageLoading: boolean
    hasNextPage: boolean
    totalImg: number | null
    handleCustomComment: (
        messages: Message | Message[],
        prepend: boolean,
        status: CommentStatus
    ) => void
    handleTotalImage: (func: (prev: number | null) => number | null) => void
    handleComments: (func: (prev: CustomComment[]) => CustomComment[]) => void
    handleImageFirstRender: (isFirst: boolean) => void
    endMessageRef: React.RefObject<HTMLLIElement>
    loadedImg: number
    imageFirstRender: boolean
    handleLoadedImg: () => void
    handleMessagesContainerScroll: (ev: React.WheelEvent<HTMLDivElement>) => void
    messagesContainerScrollToBottom: () => void
    isBottom: boolean
}

const MessagesContext = createContext<MessagesContextType | undefined>(undefined)

export const MessagesProvider = ({ children }: { children: React.ReactNode }) => {
    const { client, isTalkplusLoginComplete } = useTalkPlusClient()
    const { channelId, channelErrorHandler } = useChannel()
    const user = useRecoilValue(userState)
    // 다음 채팅 페이지가 있는지 여부
    const [hasNextPage, setHasNextPage] = useState<boolean>(false)
    // 페이지네이션이나 데이터 페칭에 사용되는 커서 값을 저장하는 상태
    const [cursor, setCursor] = useState<string | null>(null)
    // 댓글 목록을 저장하는 상태. 초기값은 빈 배열
    const [comments, setComments] = useState<CustomComment[]>([])

    // 총 이미지 카운트 - 로딩된 이미지 갯수가 해당 값과 같아진다면 스크롤
    const [totalImg, setTotalImg] = useState<number | null>(null)
    // 처음 이미지를 렌더링 하는지 여부 관리 - 첫 렌더링 시 한정 이미지가 모두 로드되면 하단으로 스크롤하기 위해 사용
    const [imageFirstRender, setImageFirstRender] = useState<boolean>(true)
    // 현재 몇 개의 이미지가 로딩 되었는지 카운트 - 해당 값이 전체 이미지와 같아지면 endMessageRef 요소로 스크롤
    const [loadedImg, setLoadedImg] = useState<number>(0)

    // 사용자가 스크롤 컨테이너의 하단에 있는지 여부를 판단하는 상태 - 해당 값이 true면 하단으로 스크롤 버튼 표시
    const [isBottom, setIsBottom] = useState(false)
    // 스크롤 컨테이너 요소에 대한 참조
    const messagesContainerRef = useRef<HTMLDivElement>(null)
    // 현재 상태를 추적할 ref 생성
    const commentsRef = useRef<CustomComment[]>([])
    // 채팅 리스트의 마지막 요소 참조 - 해당 요소로 이동함으로써 맨 아래로 이동
    const endMessageRef = useRef<HTMLLIElement>(null)

    // 이미지 로드 완료 시 로드된 이미지 개수 증가
    // 채널 최초 접속 시 이미지가 완료되면 하단으로 스크롤 하기 위해 전체 이미지 개수와 로드된 이미지 개수를 관리
    const handleLoadedImg = () => {
        setLoadedImg((prev) => prev + 1)
    }

    // 화면 최하단으로 이동하는 함수
    const messagesContainerScrollToBottom = useCallback(() => {
        setTimeout(() => {
            if (messagesContainerRef.current) {
                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
            }
        }, 50)
    }, [messagesContainerRef])

    // 채팅 리스트 컨테이너의 스크롤 이벤트 핸들러
    // 사용자가 스크롤 컨테이너의 하단에 있는지 여부를 판별해 하단으로 스크롤하는 버튼을 표시하기 위해 스크롤 이벤트 추적
    const handleMessagesContainerScroll = (ev: React.WheelEvent<HTMLDivElement>) => {
        const target = ev.target as HTMLDivElement
        const height = target.scrollHeight - target.clientHeight

        if (!isBottom && target.scrollTop < height - 100) {
            setIsBottom(true)
            return
        }

        if (isBottom && target.scrollTop > height - 100) {
            setIsBottom(false)
            return
        }
    }

    // 첫 렌더링 시 이미지가 완료되면 하단으로 스크롤후 이미지 렌더링 완료 상태 업데이트
    const handleImageFirstRender = (isFirst: boolean) => {
        setImageFirstRender(isFirst)
    }

    // 메세지를 로컬에서 사용하기위한 형식으로 변환후 채팅 내역 업데이트
    const handleCustomComment = (
        messages: Message | Message[], // Message 또는 Message[] 타입
        prepend: boolean = false, // 기본값 false
        status: CommentStatus = CommentStatus.SUCCESS
    ) => {
        if (!messages) return
        // messages가 배열인지 확인
        const normalizedMessages = Array.isArray(messages) ? messages : [messages]

        // Message[]로 변환 후 동일한 로직 수행
        const customComments: CustomComment[] = normalizedMessages.map((message) => ({
            id: message.id,
            type: message.type as unknown as CommentType,
            text: message.text,
            createdAt: message.createdAt,
            userId: message.userId,
            channelId: message.channelId,
            username: message.username,
            data: {
                imageURL: message?.data?.imageURL || '',
            },
            status,
        }))

        // 최신 상태를 유지하기 위해 useRef()를 활용
        const updatedComments = prepend
            ? [...commentsRef.current, ...customComments]
            : [...customComments, ...commentsRef.current]
        commentsRef.current = updatedComments // 최신 상태를 유지
        setComments(updatedComments)
    }

    // 채팅 내역 가져오기
    const { isLoading: messageLoading } = useQuery(
        ['message-history', user, channelId],
        async () => {
            if (!channelId || !user) return

            const messageHistoryRes = await client.getMessages({
                channelId: channelId,
                limit: 20,
            })

            setHasNextPage(messageHistoryRes.hasNext)

            return messageHistoryRes.messages
        },
        {
            onSuccess(response) {
                if (!response || !response.length) return
                setTotalImg(response.reduce((acc, cur) => (cur.data?.imageURL ? acc + 1 : acc), 0))
                setCursor(response[response.length - 1].id)
                handleCustomComment(response.reverse())
            },
            onError(err) {
                // 채팅 내역 가져오는중 에러가 발생하는 경우 에러 코드 및 에러 상태 설정
                channelErrorHandler(err)
            },
            enabled: isTalkplusLoginComplete,
        }
    )

    // 이전 대화내용 보기 버튼 핸들러
    const nextPageHandler = async () => {
        if (channelId && cursor) {
            // 렌더링 전 현재 스크롤 위치 저장
            const scrollPosition = messagesContainerRef.current?.scrollTop
            const scrollHeight = messagesContainerRef.current?.scrollHeight

            const { messages, hasNext } = await client.getMessages({
                channelId: channelId,
                limit: 20,
                lastMessageId: cursor,
            })

            // 새로운 메시지를 추가하여 렌더링
            setCursor(messages[messages.length - 1].id)
            handleCustomComment(messages.reverse())
            setHasNextPage(hasNext)
            // 새로 불러온 채팅의 이미지 개수
            const newImgCount = messages.reduce(
                (acc, cur) => (cur.data?.imageURL ? acc + 1 : acc),
                0
            )

            // 총 이미지 개수 추가
            if (newImgCount > 0 && newImgCount) {
                setTotalImg((prevCount) => (prevCount ? prevCount + newImgCount : newImgCount))
            }

            // 렌더링 후 스크롤 위치 복원
            setTimeout(() => {
                if (
                    messagesContainerRef.current &&
                    scrollPosition !== undefined &&
                    scrollHeight !== undefined
                ) {
                    const newScrollHeight = messagesContainerRef.current?.scrollHeight || 0
                    const prevScrollHeight = scrollHeight || 0
                    const prevScrollTop = scrollPosition || 0

                    const newScrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight)
                    messagesContainerRef.current?.scrollTo({ top: newScrollTop })
                }
            }, 50) // 0ms로 설정하여 다음 이벤트 루프에서 실행
        }
    }

    // 에러 채팅 삭제 핸들러
    const errorCommentDeleteHandler = (deleteId: string) => {
        setComments((comments) => {
            const filteredComments = comments?.filter((comment) => comment.id !== deleteId) || null
            commentsRef.current = filteredComments // 최신 상태를 유지
            return filteredComments
        })
    }

    // 총 이미지 개수 관리 함수
    const handleTotalImage = (func: (prev: number | null) => number | null) => {
        setTotalImg((prev) => func(prev))
    }

    // 메시지 관리 함수
    const handleComments = (func: (prev: CustomComment[]) => CustomComment[]) => {
        setComments((prev) => {
            const updatedComments = func(prev)
            commentsRef.current = updatedComments // 최신 상태를 유지
            return updatedComments
        })
    }

    return (
        <MessagesContext.Provider
            value={{
                comments,
                messagesContainerRef,
                errorCommentDeleteHandler,
                nextPageHandler,
                messageLoading,
                hasNextPage,
                totalImg,
                handleCustomComment,
                handleTotalImage,
                handleComments,
                handleImageFirstRender,
                endMessageRef,
                loadedImg,
                imageFirstRender,
                handleLoadedImg,
                handleMessagesContainerScroll,
                messagesContainerScrollToBottom,
                isBottom,
            }}
        >
            {children}
        </MessagesContext.Provider>
    )
}

export const useMessages = () => {
    const context = useContext(MessagesContext)
    if (!context) {
        throw new Error('useMessages must be used within a MessagesProvider')
    }
    return context
}
